import type { App } from 'vue'
import { getReporter } from './reporter'

/**
 * 监控 Vue 组件渲染耗时
 *
 * 开启 Vue 的 performance 模式后，Vue 会自动创建
 * vue-<component>-mount 和 vue-<component>-update 的 performance measure，
 * 通过 PerformanceObserver 监听并过滤超过 16ms（一帧）的渲染
 */
export function setupComponentPerf(app: App): void {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return

  const reporter = getReporter()
  if (!reporter || !reporter.isSampled) return

  app.config.performance = true

  try {
    const observer = new PerformanceObserver(list => {
      const currentReporter = getReporter()
      if (!currentReporter) return

      for (const entry of list.getEntries()) {
        if (!entry.name.startsWith('vue-')) continue
        if (entry.duration <= 16) continue

        const parsed = parseVueMeasureName(entry.name)
        if (!parsed) continue

        currentReporter.addComponentPerf({
          name: parsed.name,
          type: parsed.type,
          duration: entry.duration,
          pagePath: location.pathname,
          timestamp: Date.now(),
        })
      }
    })

    observer.observe({ type: 'measure', buffered: false })
  } catch {
    // 浏览器不支持，静默跳过
  }
}

/** 解析 Vue performance measure 名称 */
function parseVueMeasureName(name: string): { name: string; type: 'mount' | 'update' } | null {
  const mountMatch = name.match(/^vue-(.+)-mount$/)
  if (mountMatch?.[1]) return { name: mountMatch[1], type: 'mount' }

  const updateMatch = name.match(/^vue-(.+)-update$/)
  if (updateMatch?.[1]) return { name: updateMatch[1], type: 'update' }

  return null
}
